// Phase 5C — Capture confidence indicator (display-only).
// Shown in intake modal header and parts row; helps the user judge whether
// a vendor capture can be trusted as-is or needs verification.
//
// Confidence is derived (not authoritative). It NEVER affects pricing,
// scheduling, or any data write path.

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle2, AlertTriangle, PencilLine } from 'lucide-react';
import { cn } from '@/lib/utils';

export type CaptureConfidence = 'high' | 'partial' | 'manual';

interface Props {
  confidence: CaptureConfidence;
  /** When true (default false) shows just the icon (for tight rows). */
  compact?: boolean;
  className?: string;
}

const CONFIG: Record<
  CaptureConfidence,
  { label: string; tip: string; Icon: typeof CheckCircle2; klass: string }
> = {
  high: {
    label: 'Structured',
    tip: 'Adapter captured all key fields cleanly.',
    Icon: CheckCircle2,
    klass: 'border-success/40 text-success',
  },
  partial: {
    label: 'Partial',
    tip: 'Fallback selectors used — verify cost / part #.',
    Icon: AlertTriangle,
    klass: 'border-warning/50 text-warning',
  },
  manual: {
    label: 'Manual',
    tip: 'Typed by hand in the extension popup.',
    Icon: PencilLine,
    klass: 'border-primary/30 text-primary/80',
  },
};

export function CaptureConfidenceBadge({ confidence, compact, className }: Props) {
  const c = CONFIG[confidence];
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={cn('gap-0.5', compact ? 'h-3.5 py-0 text-[9px]' : 'h-5 text-[10px]', c.klass, className)}
          >
            <c.Icon className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
            {!compact && c.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[220px]">
          <div className="font-medium">{c.label} capture</div>
          <div className="text-muted-foreground">{c.tip}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Best-effort inference when an explicit capture_confidence isn't present
 * on the source payload (older extension builds).
 */
export function inferCaptureConfidence(input: {
  source_vendor?: string | null;
  unit_cost?: number | null;
  part_number?: string | null;
  explicit?: CaptureConfidence | null;
}): CaptureConfidence {
  if (input.explicit) return input.explicit;
  const v = (input.source_vendor ?? '').toLowerCase();
  if (v === 'manual') return 'manual';
  if (v === 'generic' || v === '') return 'partial';
  // Structured vendor but missing critical fields → partial.
  if (input.unit_cost == null || !input.part_number) return 'partial';
  return 'high';
}
